import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import PaginationControl from './PaginationControl';

export interface GridColumn<T> {
  key: string;
  header: string;
  headerStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  /** Sort identifier — defaults to `key` when omitted. */
  sortKey?: string;
}

interface Props<T> {
  columns: GridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyIcon?: React.ReactNode;
  emptyText?: string;
  rowStyle?: (row: T) => React.CSSProperties;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  sortField?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (field: string) => void;
}

const TH: React.CSSProperties = {
  padding: '11px 16px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.05em',
  color: 'var(--nx-text-muted)',
  textTransform: 'uppercase',
  textAlign: 'left',
  borderBottom: '1px solid var(--nx-border)',
};

const TD: React.CSSProperties = {
  padding: '11px 16px',
  fontSize: 13,
  verticalAlign: 'middle',
  color: 'var(--nx-text-primary)',
};

function DataGrid<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyIcon,
  emptyText = 'No data.',
  rowStyle,
  page,
  totalPages,
  onPageChange,
  sortField,
  sortDir,
  onSortChange,
}: Props<T>) {
  const hasPagination =
    !loading &&
    rows.length > 0 &&
    totalPages !== undefined &&
    totalPages > 1 &&
    onPageChange !== undefined &&
    page !== undefined;

  return (
    <>
      {hasPagination && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 16px',
          borderBottom: '1px solid var(--nx-border)',
        }}>
          <PaginationControl page={page!} totalPages={totalPages!} onPageChange={onPageChange!} />
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}>
          <span style={{
            width: 24,
            height: 24,
            border: '2px solid var(--nx-border)',
            borderTopColor: '#1A6CF6',
            borderRadius: '50%',
            animation: 'nx-spin 0.7s linear infinite',
            display: 'inline-block',
          }} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          {emptyIcon && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              {emptyIcon}
            </div>
          )}
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)' }}>{emptyText}</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {columns.map(col => {
                  const key = col.sortKey ?? col.key;
                  const isActive = sortField === key;
                  return (
                    <th
                      key={col.key}
                      style={{
                        ...TH,
                        ...col.headerStyle,
                        cursor: col.sortable && onSortChange ? 'pointer' : undefined,
                        userSelect: col.sortable && onSortChange ? 'none' : undefined,
                      }}
                      onClick={col.sortable && onSortChange ? () => onSortChange(key) : undefined}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.header}
                        {col.sortable && isActive && (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={rowKey(row)}
                  style={{ borderBottom: '1px solid var(--nx-border)', ...rowStyle?.(row) }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--nx-surface-2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  {columns.map(col => (
                    <td key={col.key} style={{ ...TD, ...col.cellStyle }}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default DataGrid;
